import {useCallback, useRef, useState} from 'react';

/**
 * Return type for {@link useAvailableHeightPx}.
 *
 * @property availableHeightPx - Remaining vertical space from the element's top
 *   edge to the bottom of the viewport, measured in **whole CSS pixels** (value
 *   is floored).  `0` while the element has not mounted yet.
 * @property callbackRef - A React **callback ref** to place on the element
 *   whose height you want to constrain.  Unlike a plain `useRef`, a callback
 *   ref fires as soon as the DOM node appears — even when it appears *after* an
 *   initial loading/skeleton early-return — so the height is always computed.
 */
type UseAvailableHeightPxReturn = {
  availableHeightPx: number;
  callbackRef: (node: HTMLDivElement | null) => void;
};

export type UseAvailableHeightPxOptions = {
  bottomInset?: number;
};

/**
 * Calculate the remaining viewport height from an element's top edge in **raw
 * CSS pixels**.
 *
 * ### Why this exists
 *
 * The platform's `useAvailableHeight` (from `platform/foundation`) returns
 * design-system *steps*, not pixels, and works through a standard `useRef` +
 * `useEffect` pattern.  That pattern breaks when the target element is rendered
 * *conditionally* — e.g. behind a loading guard that early-returns before the
 * element's JSX.  In that situation the ref is `null` when the effect runs, and
 * a `[]` dependency array means it never re-runs.
 *
 * This hook solves both problems:
 *
 * 1. It returns **raw pixels** — suitable for setting an inline `style.height`
 *    on a plain `<div>` that is not a platform `Box`.
 * 2. It uses a **callback ref** that fires exactly when the element mounts,
 *    regardless of how many renders preceded it with loading/error guards.
 *
 * ### How it works
 *
 * A `ResizeObserver` on `document.body` recalculates the remaining height
 * whenever the page layout shifts (toolbar collapse, window resize, sidebar
 * toggle, etc.).
 *
 * A `scroll` event listener on `window` (capture phase) recalculates when
 * the page scrolls.  `getBoundingClientRect().top` is viewport-relative and
 * changes with scroll position, but `ResizeObserver` only fires on size
 * changes.  The capture phase is required because the DMS app scrolls inside
 * `#dms-app-container` (`overflow-y: auto`), not `window` — capture
 * intercepts all scroll events regardless of target.  Rapid scroll events
 * are coalesced via `requestAnimationFrame` to avoid layout thrashing.
 *
 * **Stability lock:** Once the hook has computed a valid height
 * (`lastHeightRef.current > 0`), scroll events are ignored — only
 * `ResizeObserver` callbacks trigger recalculation.  This prevents a
 * feedback loop where scroll → height change → reflow → scroll would
 * cause the grid to resize on every frame during user scroll.
 *
 * To avoid unnecessary re-renders:
 *
 * - The value is **floored** to whole pixels — subpixel jitter (e.g.
 *   `749.3 → 749.7`) does not trigger a state update.
 * - A **previous-value ref** skips `setState` when the floored result is
 *   identical to the last stored value.
 *
 * The only `useCallback` in this hook stabilises the callback ref so React
 * doesn't detach/reattach it on every render, which would disconnect and
 * recreate observers and listeners unnecessarily — this is required, not a
 * premature optimisation.
 *
 * ### Pure helper
 *
 * The arithmetic is extracted into {@link calculateRemainingHeight} for
 * unit testing without a DOM.
 *
 * @param options.bottomInset - Bottom inset in pixels to subtract from available height.
 *   Defaults to {@link BOTTOM_INSET_PX} (16px). Pass `0` for full-bleed layouts (e.g. mobile).
 *
 * @example
 * ```tsx
 * function MyGrid(props: MyGridProps) {
 *   const {availableHeightPx, callbackRef} = useAvailableHeightPx();
 *
 *   if (props.isLoading) {
 *     return <Spinner />;           // callbackRef hasn't fired yet — fine
 *   }
 *
 *   return (
 *     <div
 *       ref={callbackRef}
 *       style={{height: availableHeightPx > 0 ? availableHeightPx : undefined}}
 *     >
 *       {props.children}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAvailableHeightPx(
  options?: UseAvailableHeightPxOptions
): UseAvailableHeightPxReturn {
  const bottomInset = options?.bottomInset ?? BOTTOM_INSET_PX;
  const [availableHeightPx, setAvailableHeightPx] = useState<number>(0);
  const lastHeightRef = useRef<number>(0);
  const cleanupRef = useRef<(() => void) | null>(null);

  /**
   * @mandatory Callback ref passed to a DOM element via `ref={callbackRef}`.
   * React calls callback refs on every render if the reference changes.
   * Without useCallback, each render would detach and reattach the ref,
   * disconnecting and recreating ResizeObservers and scroll listeners on every
   * render — a severe resource leak. bottomInset is the only real dependency.
   */
  const callbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      cleanupRef.current?.();
      cleanupRef.current = null;

      if (!node) {
        return;
      }

      const calculate = () => {
        const height = calculateRemainingHeight(
          node.getBoundingClientRect().top,
          window.innerHeight,
          bottomInset
        );

        if (height > 0 && height !== lastHeightRef.current) {
          lastHeightRef.current = height;
          setAvailableHeightPx(height);
        }
      };

      calculate();

      const observer = new ResizeObserver(calculate);
      observer.observe(document.body);

      let rafId = 0;
      const onScroll = () => {
        // Once a valid height has been measured, scroll events should not
        // trigger recalculation.  getBoundingClientRect().top changes on
        // every scroll tick, which would cause the grid height to continuously
        // update — creating a feedback loop where the grid resizes on every
        // frame ("rozjíždí se").  Only ResizeObserver (which fires on actual
        // layout size changes, not scroll) should update the height after
        // the initial measurement.
        if (lastHeightRef.current > 0) {
          return;
        }
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(calculate);
      };
      window.addEventListener('scroll', onScroll, {capture: true, passive: true});

      cleanupRef.current = () => {
        observer.disconnect();
        cancelAnimationFrame(rafId);
        window.removeEventListener('scroll', onScroll, {capture: true});
      };
    },
    [bottomInset]
  );

  return {availableHeightPx, callbackRef};
}

/**
 * Bottom inset (px) subtracted from the available height so the grid card
 * does not sit flush against the viewport bottom.  The value mirrors the
 * outer layout padding visible in the reference AG Grid datagrid (16 px
 * gap between the card wrapper's bottom edge and the viewport).
 */
const BOTTOM_INSET_PX = 16;

/**
 * Pure helper: compute remaining viewport height from an element's top offset.
 *
 * The result is **floored** to whole pixels so that subpixel layout jitter does
 * not cause unnecessary React state updates.  A configurable {@link bottomInset}
 * is subtracted so the grid card mirrors the reference datagrid's bottom gap.
 *
 * Exported for unit testing.  Not intended for direct use in components — use
 * {@link useAvailableHeightPx} instead.
 *
 * @param elementTop - `element.getBoundingClientRect().top` (pixels from the
 *   viewport top to the element's top border edge).
 * @param viewportHeight - `window.innerHeight` at measurement time.
 * @param bottomInset - Bottom inset in pixels (defaults to {@link BOTTOM_INSET_PX}).
 * @returns Whole CSS pixels remaining below the element's top edge.
 *   Always `>= 0`; returns `0` when the element is at or below the viewport
 *   bottom.
 */
export function calculateRemainingHeight(
  elementTop: number,
  viewportHeight: number,
  bottomInset: number = BOTTOM_INSET_PX
): number {
  const remaining = viewportHeight - elementTop - bottomInset;
  return remaining > 0 ? Math.floor(remaining) : 0;
}
